import { useEffect, useRef } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useLivekitTokenMutation } from '@/state/mutations/player/use-livekit-token';
import { shouldUseSharedLiveKitRoom } from '@/helpers/preview/shared-livekit-room.helper';
import { useHasSharedLiveKitRoom } from './shared-livekit-room.context';
import { LiveKitTrackPreview } from './livekit-track-preview';
import { LiveKitPublishPreview } from './livekit-publish-preview';

export interface LiveKitPreviewConnectorProps {
  /** LiveKit room ID for connection (entityType:entityId:onAir, e.g. "Flow:507f1f77bcf86cd799439011:onAir") */
  livekitRoomId: string;
  /** Participant identity (e.g. "user-123" or "contributor-456"). Defaults to "preview-{timestamp}" */
  participantIdentity?: string;
  /** preview = subscribe and watch tracks; publish = publish camera/mic/screenshare */
  mode: 'preview' | 'publish';
  /** Optional: filter to these track SIDs (preview mode only) */
  trackSids?: string[];
  /** Optional: only this publisher’s tracks (Mongo player id). Preview mode only. */
  publisherPlayerId?: string;
  /** Optional: show camera tracks (preview mode only). Default true. */
  showCamera?: boolean;
  /** Optional: show screenshare tracks (preview mode only). Default true. */
  showScreenshare?: boolean;
  /** Height of the preview area */
  height?: string | number;
  /** When false, hide ParticipantTile metadata and mute control. Default true. */
  showControls?: boolean;
  /** Callback when connection fails */
  onError?: (error: Error) => void;
  /** Callback when connected (publish mode) */
  onConnected?: () => void;
}

/**
 * Fetches LiveKit token and renders the appropriate preview component.
 * Reusable for: event editor (preview/publish), contributions view (publish as remote speaker).
 */
export function LiveKitPreviewConnector({
  livekitRoomId,
  participantIdentity,
  mode,
  trackSids = [],
  publisherPlayerId,
  showCamera = true,
  showScreenshare = true,
  height = '100%',
  showControls = true,
  onError,
  onConnected,
}: LiveKitPreviewConnectorProps) {
  const useSharedRoom = shouldUseSharedLiveKitRoom(useHasSharedLiveKitRoom(), mode);
  const identityRef = useRef(participantIdentity ?? `preview-${Date.now()}`);
  const identity = identityRef.current;
  const mutation = useLivekitTokenMutation();

  useEffect(() => {
    if (livekitRoomId && !useSharedRoom) {
      mutation.mutate({
        livekitRoomId,
        participantIdentity: identity,
        mode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only fetch when livekitRoomId/mode change
  }, [livekitRoomId, mode, useSharedRoom]);

  if (useSharedRoom) {
    return (
      <LiveKitTrackPreview
        trackSids={trackSids}
        publisherPlayerId={publisherPlayerId}
        showCamera={showCamera}
        showScreenshare={showScreenshare}
        height={height}
        showControls={showControls}
        onError={onError}
      />
    );
  }

  if (mutation.isError) {
    return (
      <Paper
        elevation={2}
        sx={{
          height,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          bgcolor: '#f5f5f5',
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Failed to connect
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {mutation.error instanceof Error ? mutation.error.message : 'Unknown error'}
        </Typography>
      </Paper>
    );
  }

  if (mutation.isPending || !mutation.data) {
    return (
      <Box
        sx={{
          height,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#1a1a1a',
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Connecting to LiveKit...
        </Typography>
      </Box>
    );
  }

  const { token, host } = mutation.data;

  if (mode === 'publish') {
    return (
      <LiveKitPublishPreview
        serverUrl={host}
        token={token}
        height={height}
        onError={onError}
        onConnected={onConnected}
      />
    );
  }

  return (
    <LiveKitTrackPreview
      serverUrl={host}
      token={token}
      trackSids={trackSids}
      publisherPlayerId={publisherPlayerId}
      showCamera={showCamera}
      showScreenshare={showScreenshare}
      height={height}
      showControls={showControls}
      onError={onError}
    />
  );
}
